import { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { app, authentication, HostClientType } from "@microsoft/teams-js";
import {
  teamsLightTheme,
  teamsDarkTheme,
  teamsHighContrastTheme,
  Theme,
} from "@fluentui/react-components";
import { PublicClientApplication, AccountInfo } from "@azure/msal-browser";
import { logger } from "../Logger";
import config from "../config/config";

/**
 * Returns the context of our teams application, with current theme, user information.
 *
 */
export type TeamsContextType = {
  theme: Theme;
  themeString: string;
  context: app.Context | undefined;
  msalInstance: PublicClientApplication;
  userInfo: AccountInfo | undefined;
  clientType: HostClientType | undefined;
};

export const useTeamsContext = (): TeamsContextType => {
  const [theme, setTheme] = useState<Theme>(teamsLightTheme);
  const [themeString, setThemeString] = useState<string>("default");
  const [context, setContext] = useState<app.Context | undefined>(undefined);
  const [userInfo, setUserInfo] = useState<AccountInfo | undefined>(undefined);
  const [clientType, setClientType] = useState<HostClientType | undefined>(
    undefined
  );

  logger.logTrace("Entered useTeamsContext");

  // Create a singleton MSAL PublicClientApplication instance
  const msalInstance = useMemo(() => {
    const instance = new PublicClientApplication({
      auth: {
        clientId: config.clientId ?? "",
        authority: config.authority,
        redirectUri: window.location.origin,
      },
      cache: {
        cacheLocation: "sessionStorage",
        storeAuthStateInCookie: false,
      },
    });
    instance.initialize();
    return instance;
  }, []);

  // handler to change theme
  const themeChangeHandler = useCallback((theme: string | undefined) => {
    setThemeString(theme || "default");
    switch (theme) {
      case "dark":
        setTheme(teamsDarkTheme);
        break;
      case "contrast":
        setTheme(teamsHighContrastTheme);
        break;
      case "default":
      default:
        setTheme(teamsLightTheme);
    }
  }, []);

  // subscribing to app theme change handler
  const subscribeAsync = useCallback(async () => {
    await app.initialize();
    const context = await app.getContext();
    themeChangeHandler(context.app.theme);
    setClientType(context.app.host.clientType);
    setContext(context);
    app.registerOnThemeChangeHandler(themeChangeHandler);

    // Use Teams-native SSO — works inside the Teams sandbox (no hidden iframes)
    try {
      const token = await authentication.getAuthToken();
      // Decode the JWT payload (base64url → base64 → JSON)
      const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
      const payload = JSON.parse(atob(base64));
      const account: AccountInfo = {
        homeAccountId: `${payload.oid}.${payload.tid}`,
        localAccountId: payload.oid ?? "",
        environment: "login.microsoftonline.com",
        tenantId: payload.tid ?? "",
        username: payload.upn || payload.preferred_username || "",
        name: payload.name,
      };
      msalInstance.setActiveAccount(account);
      logger.setTenantId(payload.tid);
      setUserInfo(account);
    } catch (error) {
      logger.logError("Teams getAuthToken failed: " + error);
    }
  }, [msalInstance, themeChangeHandler]);

  // subscribing during layout effect
  useLayoutEffect(() => {
    (async () => {
      await subscribeAsync();
    })();
  }, [subscribeAsync]);

  logger.logTrace("Exit useTeamsContext");

  return {
    theme,
    themeString,
    context,
    msalInstance,
    userInfo,
    clientType,
  };
};
